import { apiRequest } from './core/client';
import { FolderSchema, parseArrayWithSchema } from './schemas';
import type { StandardUnifiedRole } from '@/constants/mailbox';
import type { Folder } from '@/types/email';

export type { Folder };

// ============================================================================
// Folder Types
// ============================================================================

export type FolderRoleMappings = Partial<Record<StandardUnifiedRole, string>>;
export type FolderVisibility = 'always' | 'more' | 'never';

export interface FolderPreferences {
  accountId: string;
  roleMappings: FolderRoleMappings;
  hiddenMailboxIds: string[]; // Deprecated
  mailboxVisibility?: Record<string, FolderVisibility>;
}

export interface FolderPreferencesInput {
  roleMappings: FolderRoleMappings;
  mailboxVisibility: Record<string, FolderVisibility>;
}

export interface FolderNotificationUpdate {
  mailboxId: string;
  accountId: string;
  notificationsEnabled: boolean;
  notificationsInherited: boolean;
}

// ============================================================================
// Folder Icon/System Detection
// ============================================================================

function getFolderIcon(folderName: string, role?: string | null): string {
  const key = (role || folderName || '').toLowerCase();
  const iconMap: Record<string, string> = {
    inbox: 'inbox',
    starred: 'star',
    important: 'star',
    sent: 'send',
    drafts: 'file-text',
    spam: 'alert-octagon',
    junk: 'alert-octagon',
    trash: 'trash-2',
    deleted: 'trash-2',
    archive: 'archive',
    archives: 'archive',
    work: 'briefcase',
    contacts: 'user',
  };
  return iconMap[key] || iconMap[folderName?.toLowerCase() || ''] || 'folder';
}

function isSystemFolder(folderName: string, role?: string | null): boolean {
  const key = (role || folderName || '').toLowerCase();
  const systemFolders = new Set([
    'inbox',
    'starred',
    'important',
    'sent',
    'drafts',
    'spam',
    'junk',
    'trash',
    'deleted',
    'archive',
    'archives',
  ]);
  return systemFolders.has(key);
}

// ============================================================================
// Folder Fetching
// ============================================================================

export async function fetchFolders(accountId: string): Promise<Folder[]> {
  const data = await apiRequest<unknown[]>({
    path: '/folders',
    accountId,
  });

  // Parse and enrich with UI fields
  const folders = parseArrayWithSchema(FolderSchema, data, 'fetchFolders');
  return folders.map((folder) => ({
    ...folder,
    icon: folder.icon || getFolderIcon(folder.name, folder.role),
    systemFolder: folder.systemFolder ?? isSystemFolder(folder.name, folder.role),
    count: folder.count ?? folder.unreadCount,
  })) as unknown as Folder[];
}

// ============================================================================
// Folder Preferences
// ============================================================================

export async function updateFolderPreferences(
  accountId: string,
  payload: FolderPreferencesInput
): Promise<FolderPreferences> {
  const data = await apiRequest<unknown>({
    path: '/folder-settings',
    method: 'PUT',
    accountId,
    body: payload,
  });

  const record = data as Record<string, unknown>;
  return {
    accountId: typeof record.accountId === 'string' ? record.accountId : accountId,
    roleMappings: (record.roleMappings as FolderRoleMappings) ?? {},
    hiddenMailboxIds: Array.isArray(record.hiddenMailboxIds)
      ? (record.hiddenMailboxIds as string[])
      : [],
    mailboxVisibility: (record.mailboxVisibility as Record<string, FolderVisibility>) ?? {},
  };
}

export async function updateFolderNotificationSetting(
  accountId: string,
  folderId: string,
  enabled: boolean
): Promise<FolderNotificationUpdate> {
  const data = await apiRequest<unknown>({
    path: `/folders/${encodeURIComponent(folderId)}/notifications`,
    method: 'PATCH',
    accountId,
    body: { enabled },
  });

  const record = data as Record<string, unknown>;
  return {
    mailboxId: typeof record.mailboxId === 'string' ? record.mailboxId : folderId,
    accountId: typeof record.accountId === 'string' ? record.accountId : accountId,
    notificationsEnabled: Boolean(record.notificationsEnabled),
    notificationsInherited: Boolean(record.notificationsInherited),
  };
}

// ============================================================================
// Folder Actions
// ============================================================================

export interface MarkFolderAsReadResult {
  mailboxId: string;
  accountId: string;
  updatedCount: number;
}

export async function markFolderAsRead(
  accountId: string,
  folderId: string
): Promise<MarkFolderAsReadResult> {
  const data = await apiRequest<unknown>({
    path: `/folders/${encodeURIComponent(folderId)}/mark-read`,
    method: 'POST',
    accountId,
  });
  const record = data as Record<string, unknown>;
  return {
    mailboxId: typeof record.mailboxId === 'string' ? record.mailboxId : folderId,
    accountId: typeof record.accountId === 'string' ? record.accountId : accountId,
    updatedCount: typeof record.updatedCount === 'number' ? record.updatedCount : 0,
  };
}

export interface RenameFolderResult {
  id: string;
  name: string;
  path: string;
}

export async function renameFolder(
  accountId: string,
  folderId: string,
  newName: string
): Promise<RenameFolderResult> {
  const data = await apiRequest<unknown>({
    path: `/folders/${encodeURIComponent(folderId)}/rename`,
    method: 'PATCH',
    accountId,
    body: { name: newName },
  });
  const record = data as Record<string, unknown>;
  return {
    id: typeof record.id === 'string' ? record.id : folderId,
    name: typeof record.name === 'string' ? record.name : newName,
    path: typeof record.path === 'string' ? record.path : newName,
  };
}
